import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  ArrowLeft,
  Compass,
  Loader2,
  CheckCircle2,
  Mail,
  Sparkles,
} from "lucide-react";
import Footer from "@/components/landing/Footer";
import CookieConsent from "@/components/landing/CookieConsent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuizPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("intro"); // intro | quiz | capture | result
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [qi, setQi] = useState(0);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(true);
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/quiz/questions`);
        setQuestions(data.questions || []);
      } catch {
        /* silent */
      }
    })();
  }, []);

  const choose = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
    setTimeout(() => {
      if (qi < questions.length - 1) setQi(qi + 1);
      else setStep("capture");
    }, 250);
  };

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/quiz/submit`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        answers,
        consent,
      });
      setPersona(data.persona);
      setStep("result");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore di rete.");
    } finally {
      setLoading(false);
    }
  };

  const progressPct = step === "intro" ? 0 :
    step === "result" ? 100 :
    step === "capture" ? 90 :
    Math.round(((qi + (answers[questions[qi]?.id] ? 1 : 0)) / (questions.length + 1)) * 100);

  return (
    <main data-testid="quiz-page" className="min-h-screen bg-[#F9F8F6] text-[#0B192C]">
      <header className="border-b border-[#E5E1DA] bg-[#FFFFFF]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="font-serif-display text-xl tracking-tight">
            Metodo<span className="text-[#E89B6C]">·</span>di<span className="text-[#E89B6C]">·</span>Mare
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B192C] hover:text-[#0E7C8F]"
          >
            <ArrowLeft size={14} /> Esci
          </Link>
        </div>
        <div className="h-0.5 bg-[#E5E1DA] relative">
          <div
            className="h-full bg-[#E89B6C] transition-all duration-500"
            style={{ width: `${progressPct}%` }}
            data-testid="quiz-progress-bar"
          />
        </div>
      </header>

      {step === "intro" && (
        <Intro onStart={() => setStep("quiz")} />
      )}

      {step === "quiz" && questions.length > 0 && (
        <QuestionView
          q={questions[qi]}
          number={qi + 1}
          total={questions.length}
          value={answers[questions[qi].id]}
          onChoose={choose}
          onBack={() => qi > 0 ? setQi(qi - 1) : setStep("intro")}
        />
      )}

      {step === "capture" && (
        <CaptureView
          email={email} setEmail={setEmail}
          name={name} setName={setName}
          consent={consent} setConsent={setConsent}
          onSubmit={submit}
          loading={loading} error={error}
          onBack={() => { setStep("quiz"); setQi(questions.length - 1); }}
        />
      )}

      {step === "result" && persona && (
        <ResultView persona={persona} email={email} />
      )}

      <Footer />
      <CookieConsent />
    </main>
  );
}

function Intro({ onStart }) {
  return (
    <section className="py-20 md:py-28">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#E89B6C]/40 bg-[#E89B6C]/10 mb-6">
          <Sparkles size={14} className="text-[#E89B6C]" />
          <span className="overline text-[#E89B6C]">3 minuti · 5 domande · 100% gratuito</span>
        </div>
        <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
          Che <em className="text-[#E89B6C]">imprenditore digitale</em> sei?
        </h1>
        <p className="mt-6 text-base md:text-lg font-light text-[#3A4F6B] leading-relaxed">
          5 domande veloci per scoprire la tua rotta nel digitale. Riceverai una diagnosi personalizzata,
          una mappa dei prossimi passi e un percorso su misura nell&apos;Area Iscritti.
        </p>
        <button
          onClick={onStart}
          data-testid="quiz-start-btn"
          className="mt-10 inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#E89B6C] hover:text-[#0B192C] transition-all"
        >
          <Compass size={14} /> Iniziamo <ArrowRight size={14} />
        </button>
        <p className="mt-8 text-[10px] uppercase tracking-[0.18em] text-[#3A4F6B]">
          Nessun obbligo · Disiscrizione con 1 click
        </p>
      </div>
    </section>
  );
}

function QuestionView({ q, number, total, value, onChoose, onBack }) {
  return (
    <section className="py-16 md:py-20">
      <div className="max-w-2xl mx-auto px-6">
        <button
          onClick={onBack}
          data-testid="quiz-back-btn"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#3A4F6B] hover:text-[#0B192C] mb-8"
        >
          <ArrowLeft size={13} /> Indietro
        </button>
        <p className="overline">Domanda {number} di {total}</p>
        <h2 className="font-serif-display text-3xl md:text-4xl leading-tight mt-3" data-testid={`quiz-q-${q.id}`}>
          {q.text}
        </h2>
        <div className="mt-10 space-y-3">
          {q.options.map((o) => (
            <button
              key={o.value}
              onClick={() => onChoose(q.id, o.value)}
              data-testid={`quiz-option-${q.id}-${o.value}`}
              className={`w-full text-left p-5 border-2 transition-all group ${
                value === o.value
                  ? "border-[#E89B6C] bg-[#E89B6C]/10"
                  : "border-[#E5E1DA] hover:border-[#0B192C] bg-white"
              }`}
            >
              <span className="text-sm md:text-base font-light flex items-center gap-3">
                <span className={`flex-shrink-0 w-7 h-7 border flex items-center justify-center text-[10px] uppercase tracking-[0.18em] font-mono ${
                  value === o.value ? "bg-[#E89B6C] border-[#E89B6C] text-white" : "border-[#0B192C]/30 text-[#0B192C]/60 group-hover:border-[#0B192C] group-hover:text-[#0B192C]"
                }`}>
                  {o.value.toUpperCase()}
                </span>
                {o.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function CaptureView({ email, setEmail, name, setName, consent, setConsent, onSubmit, loading, error, onBack }) {
  return (
    <section className="py-16 md:py-20 bg-gradient-to-br from-[#0B192C] via-[#173F5F] to-[#0E7C8F] text-[#F9F8F6]">
      <div className="max-w-md mx-auto px-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#F9F8F6]/70 hover:text-[#E89B6C] mb-8"
        >
          <ArrowLeft size={13} /> Modifica risposte
        </button>
        <p className="overline text-[#E89B6C]">Ultimo passo</p>
        <h2 className="font-serif-display text-3xl md:text-4xl mt-3 leading-tight">
          Dove vuoi <em className="text-[#E89B6C]">ricevere</em> il risultato?
        </h2>
        <p className="mt-4 text-sm font-light text-[#F9F8F6]/70">
          Ti invio la diagnosi via email + l&apos;accesso gratuito all&apos;Area Iscritti con i 3 corsi inclusi.
        </p>
        <form onSubmit={onSubmit} className="mt-10 space-y-5">
          <div>
            <label className="overline block mb-2 text-[#F9F8F6]/80">Nome (opzionale)</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="quiz-name-input"
              className="w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 py-3 text-base font-light text-[#F9F8F6] focus:outline-none focus:border-b-2 focus:border-[#E89B6C]" />
          </div>
          <div>
            <label className="overline block mb-2 text-[#F9F8F6]/80">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="quiz-email-input"
              className="w-full bg-transparent border-0 border-b border-[#F9F8F6]/30 py-3 text-base font-light text-[#F9F8F6] focus:outline-none focus:border-b-2 focus:border-[#E89B6C]"
              placeholder="la-tua@email.it" />
          </div>
          <label className="flex items-start gap-3 text-xs font-light text-[#F9F8F6]/70">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
              data-testid="quiz-consent-input"
              className="mt-0.5" />
            <span>
              Accetto la <Link to="/privacy" className="text-[#E89B6C] underline">privacy policy</Link>{" "}
              e voglio ricevere consigli sul Metodo di Mare. Posso disiscrivermi con un click.
            </span>
          </label>
          {error && <p className="text-sm text-[#FF8B6E]">{error}</p>}
          <button type="submit" disabled={loading || !consent}
            data-testid="quiz-submit-btn"
            className="w-full inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#F9F8F6] disabled:opacity-60">
            {loading ? <><Loader2 size={14} className="animate-spin" /> Calcolo...</> : <><Sparkles size={14} /> Scopri il risultato <ArrowRight size={14} /></>}
          </button>
        </form>
      </div>
    </section>
  );
}

function ResultView({ persona, email }) {
  return (
    <section className="py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center">
          <p className="overline">Il tuo profilo</p>
          <p className="mt-3 text-sm uppercase tracking-[0.2em]" style={{ color: persona.color }}>{persona.label}</p>
          <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl mt-4 leading-[1.05]" data-testid="quiz-result-title">
            {persona.title}
          </h1>
          <p className="mt-8 max-w-2xl mx-auto text-lg md:text-xl font-serif italic text-[#0B192C]/80 leading-relaxed">
            {persona.headline}
          </p>
        </div>

        <div className="mt-14 bg-white border border-[#E5E1DA] p-8 md:p-12">
          <p className="overline" style={{ color: persona.color }}>La tua situazione</p>
          <p className="mt-3 text-base md:text-lg font-light text-[#0B192C] leading-relaxed">
            {persona.description}
          </p>
        </div>

        <div className="mt-8 bg-white border border-[#E5E1DA] p-8 md:p-12">
          <p className="overline text-[#E89B6C]">Prossimi 3 passi per te</p>
          <ol className="mt-4 space-y-4">
            {persona.next_steps.map((s, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 border border-[#0B192C] flex items-center justify-center font-serif-display text-lg text-[#0B192C]">
                  {i + 1}
                </span>
                <span className="text-base font-light text-[#0B192C] leading-relaxed pt-0.5">{s}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-12 text-center bg-[#F4E8D6] p-8 md:p-12">
          <p className="overline text-[#0B192C]">Il prodotto su misura per te</p>
          <p className="font-serif-display text-2xl md:text-3xl mt-3">{persona.recommended_product}</p>
          <Link
            to={persona.recommended_path}
            data-testid="quiz-cta-btn"
            className="mt-8 inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#E89B6C] hover:text-[#0B192C] transition-all"
          >
            Vai al percorso consigliato <ArrowRight size={14} />
          </Link>
        </div>

        <div className="mt-12 bg-[#0E7C8F] text-[#F9F8F6] p-8 md:p-10 flex items-start gap-4">
          <Mail size={28} className="text-[#E89B6C] flex-shrink-0" />
          <div>
            <p className="font-serif-display text-xl leading-tight">Ti ho mandato anche un&apos;email</p>
            <p className="mt-2 text-sm font-light text-[#F9F8F6]/85">
              A <strong>{email}</strong> trovi questa diagnosi + l&apos;accesso gratuito all&apos;Area Iscritti con i
              3 corsi del Metodo di Mare. Controlla anche lo spam la prima volta.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
